import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger'
import { addUserActionBreadcrumb } from '../../utils/sentryTracking'

const logger = createLogger('CatalogPublishing')
const ctx = (extra) => adminLogContext('catalog_publishing', extra)


const deepClone = (x) => JSON.parse(JSON.stringify(x));

/** Must match exactly after trim and lower-case (phrase is lowercase). */
const CONFIRM_PHRASE = 'confirm staged changes';

function buildPatches(baseline, draft) {
  const counties = [];
  const municipalities = [];
  const baseList = baseline.counties || [];
  const draftList = draft.counties || [];
  const baseById = Object.fromEntries(baseList.map((c) => [c.id, c]));

  for (const dc of draftList) {
    const bc = baseById[dc.id];
    if (!bc) continue;
    if (Boolean(dc.is_published) !== Boolean(bc.is_published)) {
      counties.push({ id: dc.id, is_published: Boolean(dc.is_published) });
    }
    const bm = Object.fromEntries((bc.municipalities || []).map((m) => [m.id, m]));
    for (const dm of dc.municipalities || []) {
      const bmu = bm[dm.id];
      if (!bmu) continue;
      if (Boolean(dm.is_published) !== Boolean(bmu.is_published)) {
        municipalities.push({ id: dm.id, is_published: Boolean(dm.is_published) });
      }
    }
  }
  return { counties, municipalities };
}

function countStaged(patches) {
  return (patches.counties?.length || 0) + (patches.municipalities?.length || 0);
}

const CatalogPublishing = () => {
  const [baseline, setBaseline] = useState({ counties: [] });
  const [draft, setDraft] = useState({ counties: [] });
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  const load = async () => {
    setError('');
    setLoading(true);
    const t0 = performance.now();
    try {
      const data = await apiService.getCatalogPublishingTree();
      const next = data || { counties: [] };
      setBaseline(deepClone(next));
      setDraft(deepClone(next));
      logger.info('Catalog publishing tree loaded', ctx({
        action: 'load_catalog_tree',
        county_count: (next.counties || []).length,
        load_time_ms: Math.round(performance.now() - t0),
      }));
    } catch (err) {
      logger.apiFailure('Failed to load catalog publishing tree', ctx({ action: 'load_catalog_tree' }), err);
      setError(err?.message || 'Failed to load catalog data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patches = useMemo(() => buildPatches(baseline, draft), [baseline, draft]);
  const stagedCount = countStaged(patches);
  const hasStaged = stagedCount > 0;
  const confirmOk =
    confirmInput.trim().toLowerCase() === CONFIRM_PHRASE.toLowerCase();

  const baselineCountyById = useMemo(
    () => Object.fromEntries((baseline.counties || []).map((c) => [c.id, c])),
    [baseline]
  );

  const filteredCounties = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const counties = draft.counties || [];
    if (!q) return counties;

    return counties
      .map((c) => {
        const countyMatch =
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.slug && c.slug.toLowerCase().includes(q)) ||
          (c.state && c.state.toLowerCase().includes(q));

        const munis = c.municipalities || [];
        const munisFiltered = munis.filter(
          (m) =>
            (m.name && m.name.toLowerCase().includes(q)) ||
            String(m.geoid || '').toLowerCase().includes(q) ||
            (m.state && m.state.toLowerCase().includes(q))
        );

        if (countyMatch) return { ...c, municipalities: munis };
        if (munisFiltered.length) return { ...c, municipalities: munisFiltered };
        return null;
      })
      .filter(Boolean);
  }, [draft, filter]);

  const handleDraftCountyToggle = (countyId, v) => {
    setDraft((prev) => ({
      ...prev,
      counties: (prev.counties || []).map((c) =>
        c.id === countyId ? { ...c, is_published: v } : c
      ),
    }));
  };

  const handleDraftMuniToggle = (countyId, muniId, v) => {
    setDraft((prev) => ({
      ...prev,
      counties: (prev.counties || []).map((c) => {
        if (c.id !== countyId) return c;
        return {
          ...c,
          municipalities: (c.municipalities || []).map((m) =>
            m.id === muniId ? { ...m, is_published: v } : m
          ),
        };
      }),
    }));
  };

  const stageBulkMunicipalities = (countyId, isPublished) => {
    setDraft((prev) => ({
      ...prev,
      counties: (prev.counties || []).map((c) => {
        if (c.id !== countyId) return c;
        return {
          ...c,
          municipalities: (c.municipalities || []).map((m) => ({
            ...m,
            is_published: isPublished,
          })),
        };
      }),
    }));
  };

  const discardStaged = () => {
    setDraft(deepClone(baseline));
    setError('');
  };

  const openConfirm = () => {
    setConfirmInput('');
    setConfirmOpen(true);
  };

  const applyStaged = async () => {
    if (!confirmOk || !hasStaged) return;
    setApplying(true);
    setError('');
    addUserActionBreadcrumb('Apply catalog publishing changes', {
      view: 'admin_publishing',
      action: 'apply_catalog_changes',
      staged_count: stagedCount,
    });
    try {
      for (const p of patches.counties) {
        await apiService.setCountyPublished(p.id, p.is_published);
      }
      for (const p of patches.municipalities) {
        await apiService.setMunicipalityPublished(p.id, p.is_published);
      }
      setConfirmOpen(false);
      setConfirmInput('');
      await load();
    } catch (err) {
      logger.apiFailure('Failed to apply catalog publishing changes', ctx({
        action: 'apply_catalog_changes',
        staged_count: stagedCount,
      }), err);
      setError(err?.message || 'Failed to apply changes. Reloading from server.');
      await load();
    } finally {
      setApplying(false);
    }
  };

  const isCountyStaged = (countyId) => {
    const b = baselineCountyById[countyId];
    const d = (draft.counties || []).find((c) => c.id === countyId);
    if (!b || !d) return false;
    if (Boolean(b.is_published) !== Boolean(d.is_published)) return true;
    const bm = Object.fromEntries((b.municipalities || []).map((m) => [m.id, m]));
    for (const dm of d.municipalities || []) {
      const bmu = bm[dm.id];
      if (bmu && Boolean(bmu.is_published) !== Boolean(dm.is_published)) return true;
    }
    return false;
  };

  const isMuniStaged = (countyId, muniId) => {
    const b = baselineCountyById[countyId];
    const d = (draft.counties || []).find((c) => c.id === countyId);
    if (!b || !d) return false;
    const bm = (b.municipalities || []).find((m) => m.id === muniId);
    const dm = (d.municipalities || []).find((m) => m.id === muniId);
    if (!bm || !dm) return false;
    return Boolean(bm.is_published) !== Boolean(dm.is_published);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Catalog publishing
      </Typography>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Typography variant="body2" sx={{ mb: 2, maxWidth: 820, opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        Changes are <strong>staged</strong> until you confirm. The county switch and municipality switches do not
        hit the server immediately. Use <strong>Review &amp; apply staged changes</strong> and type the confirmation
        phrase to save. New counties and municipalities created elsewhere default to <strong>unpublished</strong>{' '}
        in the database.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <Chip
          label={hasStaged ? `${stagedCount} staged change${stagedCount === 1 ? '' : 's'}` : 'No staged changes'}
          color={hasStaged ? 'warning' : 'default'}
          variant={hasStaged ? 'filled' : 'outlined'}
        />
        <Button variant="outlined" disabled={!hasStaged || applying} onClick={discardStaged}>
          Discard staged changes
        </Button>
        <Button variant="contained" disabled={!hasStaged || applying} onClick={openConfirm}>
          Review &amp; apply staged changes
        </Button>
      </Stack>

      <TextField
        size="small"
        placeholder="Filter by county or municipality…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ mb: 2, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Showing {filteredCounties.length} of {(draft.counties || []).length} counties (draft view)
      </Typography>

      {filteredCounties.map((c) => {
        const munis = c.municipalities || [];
        const countyStaged = isCountyStaged(c.id);
        return (
          <Accordion
            key={c.id}
            disableGutters
            elevation={0}
            sx={{
              mb: 1,
              border: '1px solid',
              borderColor: countyStaged ? 'warning.main' : 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, alignItems: 'center' }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ flexWrap: 'wrap', width: '100%', pr: 1 }}
              >
                <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
                  <Switch
                    checked={Boolean(c.is_published)}
                    disabled={applying}
                    onChange={(_, v) => handleDraftCountyToggle(c.id, v)}
                    size="small"
                  />
                </Box>
                <Typography sx={{ fontWeight: 700, minWidth: 160 }}>{c.name}</Typography>
                {countyStaged && (
                  <Chip size="small" label="Staged" color="warning" variant="outlined" />
                )}
                <Typography variant="body2" color="text.secondary">
                  {c.state}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                >
                  {c.slug}
                </Typography>
                <Typography variant="body2" sx={{ ml: 'auto' }}>
                  Reports: {c.report_count ?? 0}
                </Typography>
                <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={applying || munis.length === 0}
                    onClick={() => stageBulkMunicipalities(c.id, true)}
                  >
                    Stage publish all places
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={applying || munis.length === 0}
                    onClick={() => stageBulkMunicipalities(c.id, false)}
                  >
                    Stage unpublish all places
                  </Button>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              {munis.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No municipalities assigned to this county yet. Use{' '}
                  <strong>Municipality Assignments</strong> to assign places.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Published (draft)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>GEOID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {munis.map((m) => {
                        const mStaged = isMuniStaged(c.id, m.id);
                        return (
                          <TableRow
                            key={m.id}
                            hover
                            sx={{
                              bgcolor: mStaged ? 'warning.50' : undefined,
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Switch
                                checked={Boolean(m.is_published)}
                                disabled={applying}
                                onChange={(_, v) => handleDraftMuniToggle(c.id, m.id, v)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{m.name}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {m.geoid}
                            </TableCell>
                            <TableCell>{m.state || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Dialog
        open={confirmOpen}
        onClose={() => !applying && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1800 }}
      >
        <DialogTitle>Apply staged catalog changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to update <strong>{patches.counties.length}</strong> county record
            {patches.counties.length === 1 ? '' : 's'} and{' '}
            <strong>{patches.municipalities.length}</strong> municipality record
            {patches.municipalities.length === 1 ? '' : 's'} on the server. This includes both publishing and
            unpublishing, as staged below.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Type the following phrase exactly (letters only, spaces matter):
          </Typography>
          <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.95rem' }}>
            {CONFIRM_PHRASE}
          </Paper>
          <TextField
            fullWidth
            autoFocus
            label="Confirmation"
            placeholder={CONFIRM_PHRASE}
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            disabled={applying}
            error={confirmInput.length > 0 && !confirmOk}
            helperText={
              confirmInput.length > 0 && !confirmOk ? 'Phrase does not match' : undefined
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={applyStaged}
            disabled={!confirmOk || applying}
          >
            {applying ? 'Applying…' : 'Apply changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CatalogPublishing;
